import {customElement, property, query, state} from "lit/decorators.js";
import {html, type PropertyValues, type TemplateResult} from "lit";
import type {Maybe, Nullable} from "../../../bootstrap/common/types";
import {CalendarEntry} from "./calendarEntry";
import {EopSpinner} from "../../../page/elements/spinner";
import {schedule} from "../../../common/utils/promises";
import {FeedService} from "../feedService";
import {resolve} from "../../../container";
import {CalendarRequest} from "../requests";
import {SingleRowNarrowSetting, SingleRowSetting} from "../responsiveTileAmount";
import {Resolution} from "../../../common/resolution";
import {MultiScrollCarouselLayout} from "../../../page/elements/multiScrollCarousel";
import {UnLitElement} from "../../../common/elements";

@customElement("eop-calendar-entries-slider")
export class EopCalendarEntriesSlider extends UnLitElement {

    @property({attribute: "language"})
    public language: string = "";
    @property({attribute: "collections"})
    public collections: string = "";
    @property({attribute: "character-limit", type: Number})
    protected maxDescriptionLength: number = 180;
    @property({attribute: "size", type: String})
    public size: Maybe<string> = undefined;
    @property({attribute: "use-page-overlay", type: Boolean})
    public usePageOverlay: boolean = false;
    @property({attribute: "layout"})
    public layout: string = "";

    @state()
    private entries: CalendarEntry[] = [];
    @state()
    private limit: number;

    @query("eop-spinner")
    protected spinner: EopSpinner;

    public constructor(
        private feedService: FeedService = resolve(FeedService),
        private resolution: Resolution = resolve(Resolution)
    ) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();

        if (this.layout === "narrow") {
            this.limit = new SingleRowNarrowSetting(this.size, this.resolution).getTileAmount();
        } else {
            this.limit = new SingleRowSetting(this.size, this.resolution).getTileAmount();
        }
    }

    public render(): TemplateResult {
        return html`
            <eop-spinner></eop-spinner>
            ${this.renderMultiCarousel()}
        `;
    }

    private renderMultiCarousel(): Nullable<TemplateResult> {
        if (this.entries.isEmpty()) {
            return null;
        }
        return html`
            <div class="multi-carousel">
                <eop-multi-carousel .layout=${this.carouselLayout()} slider-content-slot-name="slider-content">
                    ${this.renderTiles()}
                </eop-multi-carousel>
            </div>
        `;
    }

    private carouselLayout(): MultiScrollCarouselLayout {
        if (this.layout === "narrow") {
            return MultiScrollCarouselLayout.NARROW_TILES;
        }
        return MultiScrollCarouselLayout.DEFAULT;
    }

    private renderTiles(): Nullable<TemplateResult[]> {
        if (this.entries.isEmpty()) {
            return null;
        }
        return this.entries.map(entry => html`
            <eop-calendar-entry-tile eop-slot="slider-content" .entry=${entry}></eop-calendar-entry-tile>`);
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        void schedule(this.spinner.spinWhile(this.loadEntries()))
            .as("fetch");
    }

    private async loadEntries(): Promise<void> {
        const request = new CalendarRequest(this.language, this.limit, 0, this.collections.parseCommaSeparated());
        const response = await this.feedService.calendarEntries(request);
        this.entries = response.entries.map(it => new CalendarEntry(it, this.maxDescriptionLength, this.usePageOverlay));
    }
}